/**
 * 高德地图 JS API v2.0 SDK 加载工具
 * 文档：https://lbs.amap.com/api/jsapi-v2/documentation
 */

// 扩展 Window 接口以包含高德地图 API
declare global {
  interface Window {
    AMap: any
    _AMapSecurityConfig: { securityJsCode: string }
  }
}

// 全局回调名称
const CALLBACK_NAME = '__AMAP_LOAD_CALLBACK__'

// SDK 加载状态
let loadPromise: Promise<void> | null = null

/**
 * 加载高德地图 JS API v2.0
 * @param timeout 超时时间（毫秒），默认 10000
 * @returns Promise<void>
 */
export const loadAmapSdk = (timeout = 10000): Promise<void> => {
  const key = String(import.meta.env.VITE_AMAP_KEY || '').trim()
  if (!key) {
    return Promise.reject(new Error('未配置 VITE_AMAP_KEY（管理端 .env）'))
  }

  // 安全密钥必须在加载 SDK 前设置；已加载时也补一次，避免热更新丢配置
  if (import.meta.env.VITE_AMAP_SECURITY) {
    window._AMapSecurityConfig = {
      securityJsCode: String(import.meta.env.VITE_AMAP_SECURITY).trim()
    }
  }

  // 已加载完成
  if (window.AMap) {
    return Promise.resolve()
  }

  // 正在加载中，返回同一个 Promise
  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      loadPromise = null
      reject(new Error('高德地图 SDK 加载超时'))
    }, timeout)

    // 全局回调
    ;(window as any)[CALLBACK_NAME] = () => {
      clearTimeout(timeoutId)
      delete (window as any)[CALLBACK_NAME]
      resolve()
    }

    // 创建 script 标签
    const script = document.createElement('script')
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(
      key
    )}&callback=${CALLBACK_NAME}&plugin=AMap.Geocoder,AMap.Geolocation,AMap.ToolBar,AMap.Scale,AMap.AutoComplete,AMap.PlaceSearch`
    script.onerror = () => {
      clearTimeout(timeoutId)
      loadPromise = null
      delete (window as any)[CALLBACK_NAME]
      reject(new Error('高德地图 SDK 加载失败'))
    }
    document.body.appendChild(script)
  })

  return loadPromise
}
